"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  parseToc,
  type TocEntry,
  type TocMode,
  type TocWarning,
  type TocWarningKind,
  tocToHtml,
  tocToText,
} from "@/lib/tool-kit/paper-formatting/table-of-contents";

/** English strings. A translated content file overrides these through `ui`. */
export const TABLE_OF_CONTENTS_GENERATOR_STRINGS = {
  modeLabel: "What are you making?",
  modes: {
    contents: "Table of contents",
    figures: "List of figures",
    tables: "List of tables",
  } satisfies Record<TocMode, string>,
  headingLabel: "Heading",
  headings: {
    contents: "Table of Contents",
    figures: "List of Figures",
    tables: "List of Tables",
  } satisfies Record<TocMode, string>,
  entriesLabel: "Entries",
  entriesHint:
    "One entry per line, ending with its page number. Show levels with # signs, outline numbers such as 2.1, or indentation. Front-matter pages can be lowercase roman numerals: iii.",
  placeholders: {
    contents: "Abstract  iii\n1 Introduction  1\n1.1 Background  2",
    figures: "Figure 1. Study timeline  12\nFigure 2. Participant flow  15",
    tables: "Table 1. Sample characteristics  10\nTable 2. Correlations  14",
  } satisfies Record<TocMode, string>,
  numberCaptions: "Add a label to captions without one (Figure 3. …)",
  captionWords: {
    contents: "",
    figures: "Figure",
    tables: "Table",
  } satisfies Record<TocMode, string>,
  widthLabel: "Plain-text width (characters)",
  trySample: "Try a sample",
  clear: "Clear",
  samples: {
    contents:
      "Abstract  iii\nAcknowledgments  iv\nList of Tables  vii\n1 Introduction  1\n1.1 Background  2\n1.2 Research questions  5\n2 Literature review  7\n2.1 Sleep and memory consolidation  8\n2.2 Sleep in university students  13\n3 Method  18\n3.1 Participants  18\n3.2 Measures  20\nReferences  45\nAppendix A: Survey items  52",
    figures:
      "Figure 1. Study timeline  12\nFigure 2. Participant flow through the study  15\nMean sleep duration by year of study  23",
    tables:
      "Table 1. Sample characteristics  19\nTable 2. Descriptive statistics for sleep measures  24\nCorrelations among study variables  27",
  } satisfies Record<TocMode, string>,
  emptyPrompt: "Type or paste your entries to build the list.",
  invalidWidth: "Enter a width from 40 to 120 characters.",
  results: "Preview",
  previewLabel: "Preview of the formatted list",
  copyRich: "Copy for Word or Google Docs",
  copyPlain: "Copy as plain text",
  copied: "Copied!",
  copyHint:
    "The rich copy is a borderless two-column table in 12-point Times New Roman: titles and leader dots on the left, page numbers aligned right. The dots are typed to fill a 6.5-inch line, so a different font or margin may need a touch-up. The plain text lines up in a monospaced font such as Courier New.",
  plainHeading: "Plain text",
  plainLabel: "Plain-text version",
  warningsHeading: "Check these",
  warnings: {
    "missing-page": "“{title}” has no page number at the end of its line.",
    "page-order":
      "“{title}” is on page {page}, earlier than the entry before it.",
    "roman-after-arabic":
      "“{title}” has a roman page number ({page}) after arabic ones. Front matter numbered i, ii, iii comes before page 1.",
  } satisfies Record<TocWarningKind, string>,
} as const;

type Strings = typeof TABLE_OF_CONTENTS_GENERATOR_STRINGS;

/** Enough leader dots to cross any preview; the overflow is clipped. */
const LEADER = ". ".repeat(160);
const MODES: readonly TocMode[] = ["contents", "figures", "tables"];

function Preview({
  entries,
  heading,
  label,
}: {
  entries: readonly TocEntry[];
  heading: string;
  label: string;
}) {
  return (
    <section
      aria-label={label}
      className="rounded-lg border border-border bg-card p-4 font-serif text-foreground sm:p-6"
    >
      {heading ? <p className="mb-3 text-center font-bold">{heading}</p> : null}
      <ul className="space-y-1">
        {entries.map((entry) => (
          <li
            className="flex items-baseline gap-1"
            key={`${entry.line}-${entry.title}`}
            style={{ paddingLeft: `${(entry.level - 1) * 1.5}rem` }}
          >
            <span className="min-w-0">{entry.title}</span>
            {entry.page === null ? null : (
              <>
                <span
                  aria-hidden="true"
                  className="min-w-6 flex-1 overflow-hidden whitespace-nowrap text-muted-foreground"
                >
                  {LEADER}
                </span>
                <span className="shrink-0 tabular-nums">{entry.page}</span>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Warnings({
  warnings,
  strings,
}: {
  warnings: readonly TocWarning[];
  strings: Strings;
}) {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
      <ul className="list-disc space-y-2 pl-5 text-foreground text-sm leading-relaxed">
        {warnings.map((warning) => (
          <li key={`${warning.kind}-${warning.line}`}>
            {t(
              strings.warnings[warning.kind],
              Object.fromEntries(
                Object.entries(warning.values).map(([key, value]) => [
                  key,
                  String(value),
                ])
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TableOfContentsGeneratorWidget() {
  const strings = useToolUi(TABLE_OF_CONTENTS_GENERATOR_STRINGS);
  const [mode, setMode] = useState<TocMode>("contents");
  const [headingEdit, setHeadingEdit] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [numberCaptions, setNumberCaptions] = useState(true);
  const [widthRaw, setWidthRaw] = useState("72");
  const deferred = useDeferredValue(text);
  const heading = headingEdit ?? strings.headings[mode];
  const widthValue = parseIntegerInput(widthRaw);
  const width =
    widthValue !== null && widthValue >= 40 && widthValue <= 120
      ? widthValue
      : null;
  const parsed = useMemo(
    () =>
      deferred.trim()
        ? parseToc(deferred, {
            mode,
            numberCaptions,
            captionWord: strings.captionWords[mode],
          })
        : null,
    [deferred, mode, numberCaptions, strings]
  );
  const plain =
    parsed && width !== null
      ? tocToText(parsed.entries, { heading, width, indent: 4 })
      : "";
  // The rich copy's plain-text twin, for targets that refuse HTML.
  const fallback = parsed
    ? tocToText(parsed.entries, { heading, width: width ?? 72, indent: 4 })
    : "";

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="toc-mode"
        onChange={(next) => {
          setMode(next);
          setHeadingEdit(null);
        }}
        options={MODES.map((value) => ({ value, label: strings.modes[value] }))}
        value={mode}
      />
      <TextField
        id="toc-heading"
        label={strings.headingLabel}
        onChange={setHeadingEdit}
        value={heading}
      />
      <TextAreaField
        hint={strings.entriesHint}
        id="toc-entries"
        label={strings.entriesLabel}
        onChange={setText}
        placeholder={strings.placeholders[mode]}
        rows={10}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "contents" ? null : (
          <Checkbox
            checked={numberCaptions}
            label={strings.numberCaptions}
            onChange={setNumberCaptions}
          />
        )}
        <TextField
          id="toc-width"
          label={strings.widthLabel}
          numeric
          onChange={setWidthRaw}
          value={widthRaw}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.samples[mode])}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {width === null ? (
        <ErrorNotice>{strings.invalidWidth}</ErrorNotice>
      ) : null}
      {parsed && parsed.entries.length > 0 ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <Preview
            entries={parsed.entries}
            heading={heading}
            label={strings.previewLabel}
          />
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              html={tocToHtml(parsed.entries, heading)}
              label={strings.copyRich}
              text={fallback}
            />
            <CopyButton
              copiedLabel={strings.copied}
              disabled={plain === ""}
              label={strings.copyPlain}
              text={plain}
            />
          </div>
          <p className={HINT}>{strings.copyHint}</p>
          <Warnings strings={strings} warnings={parsed.warnings} />
          {plain ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.plainHeading}</h3>
              <textarea
                aria-label={strings.plainLabel}
                className={`${TEXTAREA} whitespace-pre`}
                id="toc-plain"
                readOnly
                rows={Math.min(16, plain.split("\n").length + 1)}
                value={plain}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
